import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

// Check if the app is running in a native environment (iOS/Android)
const isCapacitorNative = Capacitor.isNativePlatform();

interface KeyboardContextProps {
    isKeyboardVisible: boolean;
    keyboardHeight: number;
    ignoreKeyboardEvents?: boolean;
    setIgnoreKeyboardEvents?: (value: boolean) => void;
}

// Create context
const KeyboardContext = createContext<KeyboardContextProps>({
    isKeyboardVisible: false,
    keyboardHeight: 0,
});

// Custom hook to use the keyboard context
export const useKeyboard = () => useContext(KeyboardContext);

// Provider component
export const KeyboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
    const [keyboardHeight, setKeyboardHeight] = useState(0);
    const [ignoreKeyboardEvents, setIgnoreKeyboardEvents] = useState(false);
    const ignoreKeyboardEventsRef = useRef(false);

    let keyboardCloseTimeout: NodeJS.Timeout | null = null;
    const lastScrollPosition = useRef(0);
    const lastKeyboardHeight = useRef(0);

    useEffect(() => {
        ignoreKeyboardEventsRef.current = ignoreKeyboardEvents;
    }, [ignoreKeyboardEvents]);

    useEffect(() => {
        const onKeyboardShow = (info: { keyboardHeight: number }) => {
            setIsKeyboardVisible(true);
            lastKeyboardHeight.current = info.keyboardHeight; // Save the last keyboard height
            if (keyboardCloseTimeout) { clearTimeout(keyboardCloseTimeout); }
            if (ignoreKeyboardEventsRef.current) return;
            // Get focused input & adjust scrolling
            setTimeout(() => {
                requestAnimationFrame(() => {
                    const el = document.activeElement as HTMLElement;
                    if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) {
                        el.scrollIntoView({ block: "end", behavior: "smooth" });
                        lastScrollPosition.current = window.scrollY; // Save current scroll position
                    }
                });
            }, 200);
        };
        const onKeyboardHide = () => {
            setIsKeyboardVisible(false);
        };

        let hiddenFocusTimeout: NodeJS.Timeout | null;

        const handleScroll = (reset: boolean = false) => {
            const scrollY = window.scrollY || document.documentElement.scrollTop;
            const safeInset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("var(--safe-area-inset-top)")) || 0;
            const viewPort = window.visualViewport;

            const navbar = document.querySelector(".navbar") as HTMLElement;

            if (!viewPort || reset) {
                if (navbar) {
                    navbar.style.transform = `translateY(${safeInset})`;
                }
                return;
            }

            const activeElement = document.activeElement;
            const isFocusedInput = activeElement && (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA");

            if (navbar && isFocusedInput) {
                const finalTop = safeInset + scrollY; // Adjust dynamically
                navbar.style.transform = `translateY(${finalTop})`;
            }
        };

        const viewPort = window.visualViewport;
        if (isCapacitorNative) {
            Keyboard.addListener("keyboardWillShow", (info: { keyboardHeight: number }) => {
                setKeyboardHeight(info.keyboardHeight);
                lastKeyboardHeight.current = info.keyboardHeight;
                document.documentElement.style.setProperty("--keyboard-height", `${info.keyboardHeight}px`);
            });

            Keyboard.addListener("keyboardWillHide", () => {
                setKeyboardHeight(0);
                lastKeyboardHeight.current = 0;
                document.documentElement.style.setProperty("--keyboard-height", "0px");
            });

            // ✅ Only register Capacitor listeners in native apps
            Keyboard.addListener("keyboardDidShow", onKeyboardShow);
            Keyboard.addListener("keyboardDidHide", onKeyboardHide);
        } else {
            if (!viewPort) {
                setIsKeyboardVisible(false);
                handleScroll(true);
                return;
            }

            // ✅ Use `visualViewport` for mobile web
            const handleResize = () => {
                const heightDiff = window.innerHeight - viewPort.height;
                const activeElement = document.activeElement;
                const isFocusedInput = activeElement && (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA");

                // 🔥 Improved keyboard visibility check
                const isKeyboardNowVisible = Math.abs(heightDiff) > 100 || isFocusedInput;

                if (!isKeyboardNowVisible) {
                    setIsKeyboardVisible(false);
                    handleScroll(true);
                } else {
                    setIsKeyboardVisible(true);
                    handleScroll(false);
                }
            };

            const handleScrollDefault = () => handleScroll();
            window.addEventListener("scroll", handleScrollDefault, { passive: true });
            viewPort.addEventListener("resize", handleResize);

            const handleFocusIn = (event: FocusEvent) => {
                const target = event.target as HTMLElement;
                if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
                    if (keyboardCloseTimeout) clearTimeout(keyboardCloseTimeout);
                    if (hiddenFocusTimeout) clearTimeout(hiddenFocusTimeout);
                    setIsKeyboardVisible(true);
                    handleScroll();
                    handleResize();
                }
            };

            const handleFocusOut = (_: FocusEvent) => {
                if (!document.activeElement || !(document.activeElement.tagName === "INPUT" || document.activeElement.tagName === "TEXTAREA")) {
                    keyboardCloseTimeout = setTimeout(() => {
                        setIsKeyboardVisible(false);
                        window.scrollTo(0, 0); // Force a reset
                        // document.body.style.height = "100dvh"; // Restore full height
                        handleScroll(true);
                    }, 100); // Slight delay to ensure next input isn't focused
                }
            };

            document.addEventListener("focusin", handleFocusIn);
            document.addEventListener("focusout", handleFocusOut);
            return () => {
                window.removeEventListener("scroll", handleScrollDefault);
                viewPort.removeEventListener("resize", handleResize);
                document.removeEventListener("focusin", handleFocusIn);
                document.removeEventListener("focusout", handleFocusOut);
            }
        }

        return () => {
            if (isCapacitorNative) {
                Keyboard.removeAllListeners();
            }
        };
    }, []);

    return (
        <KeyboardContext.Provider value={{
            isKeyboardVisible, keyboardHeight, ignoreKeyboardEvents,
            setIgnoreKeyboardEvents,
        }}>
            {children}
        </KeyboardContext.Provider>
    );
};

